import type { WireIn, WireOut } from "./types.js";

type Listener = (msg: WireIn) => void;
type StatusListener = (s: ConnState) => void;

export type ConnState = "connecting" | "open" | "closed";

interface Cfg {
  url: string;
  session: string;
  onMessage: Listener;
  onStatus?: StatusListener;
}

export class Conn {
  private ws: WebSocket | null = null;
  private retries = 0;
  private closed = false;
  private state: ConnState = "closed";
  private pingTimer: number | null = null;

  constructor(private readonly cfg: Cfg) {}

  start(): void {
    this.closed = false;
    this.connect();
  }

  stop(): void {
    this.closed = true;
    this.setState("closed");
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.pingTimer = null;
    this.ws?.close();
    this.ws = null;
  }

  send(msg: WireOut): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      return true;
    }
    return false;
  }

  private setState(s: ConnState) {
    if (this.state === s) return;
    this.state = s;
    this.cfg.onStatus?.(s);
  }

  private connect() {
    if (this.closed) return;
    this.setState("connecting");
    const ws = new WebSocket(this.cfg.url);
    this.ws = ws;
    ws.onopen = () => {
      this.retries = 0;
      this.setState("open");
      ws.send(JSON.stringify({ type: "hello", session: this.cfg.session }));
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
      }, 25000);
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data) as WireIn;
        this.cfg.onMessage(msg);
      } catch {
        /* ignora */
      }
    };
    ws.onclose = () => {
      if (this.pingTimer) {
        clearInterval(this.pingTimer);
        this.pingTimer = null;
      }
      this.ws = null;
      if (this.closed) return;
      this.setState("closed");
      const delay = Math.min(10000, 500 * 2 ** Math.min(this.retries, 5));
      this.retries++;
      window.setTimeout(() => this.connect(), delay);
    };
    ws.onerror = () => {
      // onclose virá em seguida; tratamos lá.
    };
  }
}
